import { motion, useReducedMotion } from "framer-motion";

/**
 * DigitalTransformation — "The Journey"
 * Two states (Today / Future) connected by a curved path with 3 milestones.
 * A pulse continuously traverses the path, communicating a controlled
 * journey from the current state to the target state.
 */
export default function TransformationJourney({ className = "" }) {
  const reduce = useReducedMotion();
  // Curved path from left island to right island
  const d = "M 60 200 C 130 60, 270 260, 340 100";

  return (
    <div className={`pointer-events-none select-none ${className}`} aria-hidden="true">
      <svg viewBox="0 0 400 320" className="h-full w-full" role="presentation">
        <defs>
          <radialGradient id="jn-today" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#64748B" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#64748B" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="jn-future" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="jn-path" x1="0" x2="1">
            <stop offset="0%" stopColor="#64748B" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="1" />
          </linearGradient>
        </defs>

        {/* Ambient state glows */}
        <circle cx="60" cy="200" r="70" fill="url(#jn-today)" />
        <circle cx="340" cy="100" r="90" fill="url(#jn-future)" />

        {/* Path — dashed baseline + animated fill */}
        <path d={d} fill="none" stroke="#334155" strokeWidth="1" strokeDasharray="2 4" />
        <motion.path
          d={d}
          fill="none"
          stroke="url(#jn-path)"
          strokeWidth="1.5"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={reduce ? { pathLength: 1 } : { pathLength: [0, 1, 1] }}
          transition={reduce ? { duration: 0 } : { duration: 7, times: [0, 0.7, 1], repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Milestone diamonds along the path (approximate points from the cubic) */}
        {[
          { x: 130, y: 120, label: "OPERATE" },
          { x: 200, y: 160, label: "MODERNISE" },
          { x: 280, y: 170, label: "SCALE" },
        ].map((m, i) => (
          <g key={m.label}>
            <motion.g
              initial={{ opacity: 0, scale: 0.6 }}
              animate={reduce ? { opacity: 1, scale: 1 } : { opacity: [0, 1, 1, 0.4], scale: [0.6, 1, 1, 0.9] }}
              transition={reduce ? { duration: 0 } : { duration: 7, times: [0, 0.25 + i * 0.15, 0.75, 1], repeat: Infinity, ease: "easeOut" }}
              style={{ transformOrigin: `${m.x}px ${m.y}px`, transformBox: "fill-box" }}
            >
              <rect x={m.x - 5} y={m.y - 5} width="10" height="10" transform={`rotate(45 ${m.x} ${m.y})`} fill="#93C5FD" />
            </motion.g>
            <text x={m.x} y={m.y - 14} textAnchor="middle" fill="#CBD5E1" fontSize="8" letterSpacing="1.5" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
              {m.label}
            </text>
          </g>
        ))}

        {/* Endpoints — today island (dim) and future island (bright) */}
        <g>
          <circle cx="60" cy="200" r="14" fill="none" stroke="#94A3B8" strokeOpacity="0.6" strokeWidth="1.2" strokeDasharray="3 3" />
          <circle cx="60" cy="200" r="5" fill="#94A3B8" />
          <text x="60" y="228" textAnchor="middle" fill="#94A3B8" fontSize="9" letterSpacing="2" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
            TODAY
          </text>
        </g>
        <g>
          <motion.circle
            cx="340" cy="100" r="16" fill="none" stroke="#3B82F6" strokeWidth="1.5"
            initial={{ opacity: 0.5 }}
            animate={reduce ? { opacity: 1 } : { opacity: [0.5, 1, 0.5] }}
            transition={reduce ? { duration: 0 } : { duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <circle cx="340" cy="100" r="7" fill="#3B82F6" />
          <text x="340" y="80" textAnchor="middle" fill="#93C5FD" fontSize="9" letterSpacing="2" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
            FUTURE STATE
          </text>
        </g>

        {/* Travelling pulse along the path */}
        {!reduce && (
          <>
            <circle r="3.5" fill="#EFF6FF">
              <animateMotion dur="6s" repeatCount="indefinite" rotate="auto" path={d} />
              <animate attributeName="opacity" values="0;1;1;0" dur="6s" repeatCount="indefinite" />
            </circle>
            <circle r="6" fill="#3B82F6" fillOpacity="0.35">
              <animateMotion dur="6s" repeatCount="indefinite" rotate="auto" path={d} />
              <animate attributeName="opacity" values="0;0.6;0.6;0" dur="6s" repeatCount="indefinite" />
            </circle>
          </>
        )}
      </svg>
    </div>
  );
}
